// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet, View } from 'react-native';
import { useMemo } from 'react';
import { useGlobalSearchParams, usePathname, useRouter } from 'expo-router';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';

export function BottomMenu() {
  const router = useRouter();
  const pathname = usePathname();
  const safePathname = pathname ?? '';
  const { tourId: tourParam, debug: debugParam } = useGlobalSearchParams<{ tourId?: string; debug?: string }>();
  const roundId = useRoundState((state) => state.roundId);
  const holeNumber = useRoundState((state) => state.hole);
  const { t } = useI18n();
  const roundIdFromPath = useMemo(() => {
    const parts = safePathname.split('/').filter(Boolean);
    const idx = parts.indexOf('round');
    if (idx < 0) return undefined;
    return parts[idx + 1];
  }, [safePathname]);
  const activeRoundId = roundId ?? roundIdFromPath;

  const handleTourPress = () => {
    const tourId = getCurrentTourId();
    if (tourId) {
      router.push(`/tour/${tourId}/leaderboard`);
    } else {
      router.push('/tournaments');
    }
  };

  const handleHolePress = () => {
    const params: string[] = [];
    if (debugParam === 'true') params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    if (activeRoundId) {
      const nextHole = holeNumber ?? 1;
      router.push(`/round/${activeRoundId}/hole/${nextHole}/compact${query}`);
      return;
    }
    router.push('/tournaments');
  };

  const handleScorecardPress = () => {
    if (activeRoundId) {
      router.push(`/round/${activeRoundId}/scorecard`);
      return;
    }
    router.push('/tournaments');
  };

  const handleRoundResultsPress = () => {
    if (activeRoundId) {
      const parts: string[] = [];
      if (tourParam) parts.push(`tourId=${tourParam}`);
      if (debugParam === 'true') parts.push('debug=true');
      const query = parts.length ? `?${parts.join('&')}` : '';
      router.push(`/round/${activeRoundId}/summary${query}`);
      return;
    }
    router.push('/tournaments');
  };

  const handleLayoutTogglePress = () => {
    if (!activeRoundId) {
      router.push('/tournaments');
      return;
    }
    const isSwipeLayout = safePathname.endsWith('/score');
    const isCompactLayout = safePathname.endsWith('/compact');
    const targetHole = holeNumber ?? 1;
    const parts: string[] = [];
    if (debugParam === 'true') parts.push('debug=true');
    let targetPath = `/round/${activeRoundId}/hole/${targetHole}/compact`;
    if (isCompactLayout) {
      targetPath = `/round/${activeRoundId}/hole/${targetHole}`;
      parts.push('layout=standard');
    } else if (isSwipeLayout) {
      targetPath = `/round/${activeRoundId}/hole/${targetHole}/compact`;
    } else {
      targetPath = `/round/${activeRoundId}/hole/${targetHole}/score`;
    }
    const query = parts.length ? `?${parts.join('&')}` : '';
    router.push(`${targetPath}${query}`);
  };
  const isHoleRoute = safePathname.includes('/hole/');

  return (
    <View style={styles.container}>
      <Pressable style={styles.item} onPress={handleHolePress}>
        <FontAwesome name="flag-checkered" size={18} color="#c8e1ff" />
        <Text style={styles.label}>{t('menu.hole')}</Text>
      </Pressable>
      {isHoleRoute ? (
        <Pressable style={styles.item} onPress={handleLayoutTogglePress}>
          <FontAwesome name="th-large" size={18} color="#9bb3ff" />
          <Text style={styles.label}>{t('menu.layout')}</Text>
        </Pressable>
      ) : null}
      <Pressable style={styles.item} onPress={handleTourPress}>
        <FontAwesome name="trophy" size={18} color="#d3b36c" />
        <Text style={styles.label}>{t('menu.tour')}</Text>
      </Pressable>
      <Pressable style={styles.item} onPress={handleRoundResultsPress}>
        <FontAwesome name="list-alt" size={18} color="#f1c27b" />
        <Text style={styles.label}>{t('menu.roundResults')}</Text>
      </Pressable>
      <Pressable style={styles.item} onPress={handleScorecardPress}>
        <FontAwesome name="th" size={18} color="#8fd0ff" />
        <Text style={styles.label}>{t('menu.scorecard')}</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 56,
    backgroundColor: '#0c1429',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 16,
  },
  item: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  label: {
    color: '#e6eefc',
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
});
